import React, { useState } from "react";
import { useAppDispatch } from "../../../store/hooks";
import {
    toggleTask,
    deleteTask,
    deleteTeamNote,
} from "../../../store/slices/teamFocusSessionSlice";
import { Note } from "../../../store/slices/teamFocusSession/types";
import TasksTab from "./TasksTab";
import NotesTab from "./NotesTab";
import TeamModeCheckins from "./TeamModeCheckins";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { deleteTeamTask, deleteTeamCheckin } from "../../../store/slices/teamFocusSession/index";
import { toast } from "react-toastify";

interface SessionTabsProps {
    teamId: string;
    session: any;
    onEditTask?: (task: any) => void;
    onEditNote?: (note: Note) => void;
}

const SessionTabs: React.FC<SessionTabsProps> = ({ teamId, session, onEditTask, onEditNote }) => {
    const dispatch = useAppDispatch();

    const [sessionTab, setSessionTab] = useState<"notes" | "tasks" | "reminders">(
        "tasks",
    );

    const [deleteModal, setDeleteModal] = useState<{
        isOpen: boolean;
        type: "task" | "note" | "checkin" | null;
        id: string | null;
        loading: boolean;
    }>({
        isOpen: false,
        type: null,
        id: null,
        loading: false
    });

    const openDeleteModal = (type: "task" | "note" | "checkin", id: string) => {
        setDeleteModal({ isOpen: true, type, id, loading: false });
    };

    const closeDeleteModal = () => {
        setDeleteModal(prev => ({ ...prev, isOpen: false }));
    };

    const handleDeleteConfirm = async () => {
        if (!deleteModal.id || !deleteModal.type) return;

        setDeleteModal(prev => ({ ...prev, loading: true }));
        try {
            if (deleteModal.type === "task") {
                await dispatch(deleteTeamTask({ teamId, taskId: deleteModal.id })).unwrap();
                toast.success("Task deleted successfully");
            } else if (deleteModal.type === "note") {
                await dispatch(deleteTeamNote({ teamId, noteId: deleteModal.id })).unwrap();
                toast.success("Note deleted successfully");
            } else if (deleteModal.type === "checkin") {
                await dispatch(deleteTeamCheckin({ teamId, checkinId: deleteModal.id })).unwrap();
                toast.success("Check-in deleted successfully");
            }
            closeDeleteModal();
        } catch (error: any) {
            toast.error(error || `Failed to delete ${deleteModal.type}`);
        } finally {
            setDeleteModal(prev => ({ ...prev, loading: false }));
        }
    };


    if (!session) return null;

    const tasks = session.tasks || [];
    const notes = Array.isArray(session.notes) ? session.notes : [];
    const notesLoading = session.notesLoading;
    const tasksLoading = session.tasksLoading;

    return (
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="flex w-full border-b border-gray-200/60 px-4 pt-4">
                {["tasks", "notes", "reminders"].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setSessionTab(tab as any)}
                        className={`flex-1 py-3 font-medium transition-all text-sm capitalize relative
                                        ${sessionTab === tab ? "text-orange-600" : "text-gray-500 hover:text-gray-700"}
                                        focus:outline-none focus:ring-0 active:outline-none active:ring-0`}
                    >
                        {tab === "reminders" ? "Check-ins" : tab}
                        {sessionTab === tab && (
                            <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-orange-600" />
                        )}
                    </button>
                ))}
            </div>

            <div className="p-6 flex-1 overflow-y-auto">
                {sessionTab === "tasks" && (
                    <TasksTab
                        tasks={tasks}
                        loading={tasksLoading}
                        session={session}
                        teamId={teamId}
                        onToggleTask={(taskId) =>
                            dispatch(toggleTask({ teamId, taskId }))
                        }
                        onDeleteTask={(taskId) => openDeleteModal("task", taskId)}
                        onEditTask={onEditTask}
                    />
                )}

                {sessionTab === "notes" &&
                    (notesLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <span className="text-gray-500">Loading notes...</span>
                        </div>
                    ) : (
                        <NotesTab
                            notes={notes}
                            onDeleteNote={(noteId) => openDeleteModal("note", noteId)}
                            onEditNote={onEditNote}
                        />
                    ))}
                {sessionTab === "reminders" && (
                    <div>
                        <TeamModeCheckins
                            teamId={teamId}
                            onDeleteCheckin={(id) => openDeleteModal("checkin", id)}
                        />
                    </div>
                )}
            </div>

            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={closeDeleteModal}
                onConfirm={handleDeleteConfirm}
                loading={deleteModal.loading}
                title={`Delete ${deleteModal.type === 'checkin' ? 'Check-in' : (deleteModal.type ? deleteModal.type.charAt(0).toUpperCase() + deleteModal.type.slice(1) : 'Item')}`}
                description={`Are you sure you want to delete this ${deleteModal.type || 'item'}? This action cannot be undone.`}
            />
        </div>
    );
};

export default SessionTabs;
